import React, { useCallback, useState } from 'react';
import { Archive, ShieldAlert } from 'lucide-react';
import ArbeitsvertragModal from '../components/pe-papers/ArbeitsvertragModal';
import KuendigungModal from '../components/pe-papers/KuendigungModal';
import UnwiderruflicheFreistellungModal from '../components/pe-papers/UnwiderruflicheFreistellungModal';
import AbmahnungModal from '../components/modals/AbmahnungModal';
import ArbeitszeugnisModal from '../components/modals/ArbeitszeugnisModal';
import ElternzeitModal from '../components/modals/UrlaubskuerzungElternzeitModal';
import VertragsAnpassungKombiModal from '../components/modals/VertragsAnpassungKombiModal';
import FortbildungModal from '../components/pe-papers/FortbildungModal';
import ModellReleaseModal from '../components/pe-papers/ModellReleaseModal';
import MitfuehrpflichtModal from '../components/pe-papers/MitfuehrpflichtModal';
import PEPapersInstructionModal from '../components/pe-papers/PEPapersInstructionModal';
import PEInvoiceModal from '../components/pe-papers/PEInvoiceModal';
import BgwAssessmentModal from '../components/pe-papers/BgwAssessmentModal';
import BgwUvvUnterweisungModal from '../components/pe-papers/BgwUvvUnterweisungModal';
import ConsultationQuoteModal from '../components/pe-papers/ConsultationQuoteModal';
import DsgvoVvtModal from '../components/pe-papers/DsgvoVvtModal';
import GobdModal from '../components/pe-papers/GobdModal';
import PeSachbezugModal from '../components/pe-papers/PeSachbezugModal';
import PePapersArchivPanel from '../components/pe-papers/PePapersArchivPanel';
import PePapersStorageHint, {
    PE_PAPERS_STORAGE_HINTS,
    type PePapersStorageHintData,
} from '../components/pe-papers/PePapersStorageHint';
import useAppStore from '../store/appStore';
import type { PePapersArchiveDoc } from '../services/pePapersArchiveService';

const PePapersPageStyles = () => (
    <style>{`
        .carbon-weave-bg {
            background-color: #111;
            background-image:
                radial-gradient(circle at 100% 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 0 150%, #111 24%, #1a1a1a 24%, #1a1a1a 28%, #111 28%, #111 36%, #1a1a1a 36%, #1a1a1a 40%, transparent 40%, transparent),
                radial-gradient(circle at 50% 100%, #1a1a1a 10%, #111 10%, #111 23%, #1a1a1a 23%, #1a1a1a 30%, #111 30%, #111 43%, #1a1a1a 43%, #1a1a1a 50%, #111 50%, #111 63%, #1a1a1a 63%, #1a1a1a 71%, transparent 71%, transparent),
                radial-gradient(circle at 100% 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent),
                radial-gradient(circle at 0 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent);
            background-size: 20px 20px;
        }

        .carbon-panel {
            position: relative;
            border: 1px solid #2a2a2a;
            box-shadow: 0 30px 60px -15px rgba(0,0,0,1), inset 0 2px 5px rgba(255,255,255,0.05);
        }

        .carbon-panel::after {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.6));
            border-radius: inherit;
            pointer-events: none;
        }

        .carbon-content {
            position: relative;
            z-index: 10;
        }

        /* ============================================================
           LIGHT-MODE OVERRIDES (Ring 4c) — Dark Mode bleibt pixelidentisch,
           da alle Regeln nur unter html:not(.dark) greifen.
           ============================================================ */
        html:not(.dark) .carbon-weave-bg {
            background-color: #FFFFFF;
            background-image: none;
        }
        html:not(.dark) .carbon-panel {
            border: 1px solid #D6D3D1;
            box-shadow: 0 10px 30px -12px rgba(28,25,23,0.15);
        }
        html:not(.dark) .carbon-panel::after {
            background: none;
        }

        /* Alle Modul-Karten: helles Weiß gegen Warm-Platin-Seite (Handy-Kontrast) */
        html:not(.dark) .pepapers-module-card {
            background-color: #FFFFFF !important;
            border-color: #C8C2BC !important;
            box-shadow: 0 12px 28px -14px rgba(28, 25, 23, 0.16);
        }

        /* Titelstreifen: Warm-Platin mit Kante gegen weißes Kartenfeld */
        html:not(.dark) .pepapers-title-strip {
            background-color: #F5F0EA !important;
            background-image: none !important;
            border: 1px solid #D6D3D1;
            box-shadow: inset 0 1px 0 rgba(255,255,255,0.7);
        }
        html:not(.dark) .pepapers-card-warn {
            border-color: #F1A8B5 !important;
            box-shadow: 0 12px 28px -14px rgba(159, 18, 57, 0.18);
            background-color: #FFFBFB !important;
        }
        html:not(.dark) .pepapers-cta-default {
            color: #44403C !important;
            border-color: #A8A29E !important;
            background: #FAFAF9 !important;
            font-weight: 600;
        }
        html:not(.dark) .pepapers-cta-default:hover {
            background: #F5F5F4 !important;
            border-color: #78716C !important;
        }
        html:not(.dark) .pepapers-badge-warn {
            color: #9F1239 !important;
            background: #FFE4E6 !important;
            border-color: #FB7185 !important;
            font-weight: 800;
        }
        html:not(.dark) .pepapers-cta-warn {
            color: #9F1239 !important;
            border-color: #E11D48 !important;
            background: #FFF1F2 !important;
            font-weight: 700;
        }
        html:not(.dark) .pepapers-cta-warn:hover {
            background: #FFE4E6 !important;
        }
        html:not(.dark) .pepapers-desc {
            color: #44403C !important;
        }
    `}</style>
);

interface PePapersModule {
    id: string;
    title: string;
    /** Optionaler Zeilenumbruch im Karten-Titel (lange Komposita). */
    titleLines?: string[];
    description: string;
    statusText: string;
    isPremium: boolean;
    /** Optional badge override (default: Premium) */
    badgeLabel?: string;
    /** Warn-Akzent für gesetzliche Pflicht-Karten */
    warnAccent?: boolean;
    showShieldIcon?: boolean;
    /** Aufbewahrung / Aushang — nur UI, nie im PDF */
    storageHint?: PePapersStorageHintData;
}

const PE_PAPERS_MODULES: PePapersModule[] = [
    {
        id: 'bgw',
        title: 'BGW-Gefährdungsbeurteilung',
        titleLines: ['BGW-', 'Gefährdungsbeurteilung'],
        description:
            'Gesetzlich vorgeschrieben gem. § 5 ArbSchG, DGUV V2 & TRGS 530. Fehlende oder lückenhafte Beurteilungen führen bei Kontrollen der BGW und Gewerbeaufsicht zu empfindlichen Bußgeldern (bis zu 25.000 €) und vollem Haftungsregress bei Arbeitsunfällen. In unter 3 Minuten rechtssicher dokumentiert.',
        statusText: 'Gesetzliche Pflicht',
        isPremium: true,
        badgeLabel: 'Gesetzliche Pflicht',
        warnAccent: true,
        showShieldIcon: true,
        storageHint: PE_PAPERS_STORAGE_HINTS.bgw,
    },
    {
        id: 'bgw-uvv-unterweisung',
        title: 'BGW- & UVV-Unterweisung',
        titleLines: ['BGW- & UVV-', 'Unterweisung'],
        description:
            'Rechtssichere Jahresunterweisung gem. § 12 ArbSchG, DGUV V1 & TRGS 530. Lückenlose Dokumentation für BGW & Gewerbeaufsicht inkl. Hautschutzplan, Werkzeug-Sicherheit & Azubi-Schutz.',
        statusText: 'Gesetzliche Pflicht',
        isPremium: true,
        badgeLabel: 'Gesetzliche Pflicht',
        warnAccent: true,
        showShieldIcon: true,
        storageHint: PE_PAPERS_STORAGE_HINTS['bgw-uvv-unterweisung'],
    },
    {
        id: 'dsgvo',
        title: 'DSGVO-Verfahrensdokumentation',
        titleLines: ['DSGVO-', 'Verfahrensdokumentation'],
        description:
            'Verfahrensdokumentation nach DSGVO (Art. 30) & TOM-Nachweis (Art. 32). Fehlende Dokumentation droht mit Bußgeldern bis zu 20 Mio. € bzw. 4 % des weltweiten Vorjahresumsatzes (Art. 83 DSGVO). In unter 2 Minuten rechtssicher dokumentiert.',
        statusText: 'Gesetzliche Pflicht',
        isPremium: true,
        badgeLabel: 'Gesetzliche Pflicht',
        warnAccent: true,
        showShieldIcon: true,
        storageHint: PE_PAPERS_STORAGE_HINTS.dsgvo,
    },
    {
        id: 'gobd',
        title: 'GoBD-Verfahrensdokumentation',
        titleLines: ['GoBD-', 'Verfahrensdokumentation'],
        description:
            'Verfahrensdokumentation zur Kassenführung gem. §§ 146, 147 AO & KassenSichV für Kassennachschau und Betriebsprüfung.',
        statusText: 'Gesetzliche Pflicht',
        isPremium: true,
        badgeLabel: 'Gesetzliche Pflicht',
        warnAccent: true,
        showShieldIcon: true,
        storageHint: PE_PAPERS_STORAGE_HINTS.gobd,
    },
    {
        id: 'sachbezug-50',
        title: '50-€-Sachbezugsvereinbarung',
        description:
            'Rechtssichere Zusatzvereinbarung für steuerfreien Sachbezug gem. § 8 Abs. 2 EStG / ZAG für die Lohnakte.',
        statusText: 'Gesetzliche Pflicht',
        isPremium: true,
        badgeLabel: 'Gesetzliche Pflicht',
        warnAccent: true,
        showShieldIcon: true,
        storageHint: PE_PAPERS_STORAGE_HINTS['sachbezug-50'],
    },
    {
        id: 'mitfuehrpflicht-zeiterfassung',
        title: 'SAMMELBESTÄTIGUNG: MITFÜHRPFLICHT & ZEITERFASSUNG',
        description: 'Dokumentation zur gesetzlichen Ausweis-Mitführpflicht (§ 2a SchwarzArbG) und Verpflichtung zur revisionssicheren Arbeitszeiterfassung.',
        statusText: 'Gesetzliche Pflicht',
        isPremium: true,
        badgeLabel: 'Gesetzliche Pflicht',
        warnAccent: true,
        showShieldIcon: true,
        storageHint: PE_PAPERS_STORAGE_HINTS['mitfuehrpflicht-zeiterfassung'],
    },
    {
        id: 'pe-invoice',
        title: 'Rechnung / Kostenvoranschlag',
        description:
            'Rechnungen und Kostenvoranschläge mit Brutto/Netto-Automatik, Kundentyp-Steuerung & GiroCode erstellen.',
        statusText: 'Bereit zur Konfiguration',
        isPremium: true,
        badgeLabel: 'Rechnung',
    },
    {
        id: 'consultation-quote',
        title: 'High-Ticket Beratung & Kostenvoranschlag',
        titleLines: ['High-Ticket Beratung &', 'Kostenvoranschlag'],
        description:
            'Rechtssicherer Behandlungsvertrag mit Haaranalyse, flexibler Anzahlung und § 615 BGB No-Show-Ausfallschutz. Verhindert Terminausfälle und Streitigkeiten bei Balayage & Farbkorrekturen.',
        statusText: 'Bereit zur Konfiguration',
        isPremium: true,
        badgeLabel: 'Beratung',
    },
    {
        id: 'arbeitsvertrag',
        title: 'Arbeitsvertrag',
        description: 'Vertrag für dein Salon-Team inkl. Leistungslohn – mandantenspezifisch und rechtssicher vorbereitet.',
        statusText: 'Bereit zur Konfiguration',
        isPremium: true,
        badgeLabel: 'Personal',
    },
    {
        id: 'vertragsanpassung',
        title: 'Änderungsvertrag / Änderungskündigung',
        description:
            'Anpassungen am Arbeitsverhältnis per Änderungsvertrag oder Änderungskündigung – mehrere Bereiche kombinierbar (Arbeitszeit, Vergütung, Filiale).',
        statusText: 'Bereit zur Konfiguration',
        isPremium: true,
        badgeLabel: 'Personal',
    },
    {
        id: 'kuendigung',
        title: 'Kündigung / Aufhebungsvertrag',
        description: 'Beendigung oder einvernehmliche Auflösung eines Arbeitsverhältnisses – klar dokumentiert für dich als Arbeitgeber.',
        statusText: 'Bereit zur Konfiguration',
        isPremium: true,
        badgeLabel: 'Personal',
    },
    {
        id: 'unwiderrufliche-freistellung',
        title: 'Unwiderrufliche Freistellung',
        description:
            'Rechtssichere Freistellung nach Eigen- oder Arbeitgeberkündigung inkl. Anrechnung von Resturlaub und Überstunden.',
        statusText: 'Bereit zur Konfiguration',
        isPremium: true,
        badgeLabel: 'Personal',
    },
    {
        id: 'abmahnung',
        title: 'Abmahnung / Ermahnung',
        description: 'Verwarnungen bei Regelverstößen im Salonalltag – sachlich, nachvollziehbar und rechtssicher formuliert.',
        statusText: 'Bereit zur Konfiguration',
        isPremium: true,
        badgeLabel: 'Personal',
    },
    {
        id: 'arbeitszeugnis',
        title: 'Arbeitszeugnis',
        description: 'Von Stichpunkten zum fertigen Zeugnis – für Mitarbeiterinnen und Mitarbeiter deines Salons.',
        statusText: 'Bereit zur Konfiguration',
        isPremium: true,
        badgeLabel: 'Personal',
    },
    {
        id: 'urlaubskuerzung',
        title: 'Urlaubskürzung Elternzeit',
        description: 'Urlaubsanpassung in der Elternzeit nach § 17 MuSchG – präzise für deinen Salonbetrieb.',
        statusText: 'Bereit zur Konfiguration',
        isPremium: true,
        badgeLabel: 'Personal',
    },
    {
        id: 'fortbildungsrueckzahlung',
        title: 'Fortbildungsrückzahlung',
        description:
            'Vereinbarung zur Fortbildungsrückzahlung mit automatischer Bindungsdauer und Abschmelzungstabelle.',
        statusText: 'Bereit zur Konfiguration',
        isPremium: true,
        badgeLabel: 'Personal',
    },
    {
        id: 'modell-release',
        title: 'MODELL-RELEASE-VERTRAG',
        description: 'Rechtssichere Vereinbarung für Foto- und Videoaufnahmen von Modellen zu Marketing- und Werbezwecken im Salon.',
        statusText: 'Bereit zur Konfiguration',
        isPremium: true,
        badgeLabel: 'Marketing',
    },
    {
        id: 'dienstanweisung-social-media',
        title: 'DIENSTANWEISUNG: SOCIAL MEDIA & SALON-EQUIPMENT',
        description: 'Klare Spielregeln für das Team: Regelt die erlaubte Nutzung von Salon-iPads, privaten Smartphones während der Arbeitszeit und Richtlinien für Social-Media-Posts.',
        statusText: 'Bereit zur Konfiguration',
        isPremium: true,
        badgeLabel: 'Betrieb',
    },
];

const PePapersPage: React.FC = () => {
    const [activeModal, setActiveModal] = useState<string | null>(null);
    const [archivOpen, setArchivOpen] = useState(false);
    const [archiveDoc, setArchiveDoc] = useState<PePapersArchiveDoc | null>(null);
    const [archivRefreshKey, setArchivRefreshKey] = useState(0);
    const openPremiumModal = useAppStore((state) => state.openPremiumModal);

    const closeModal = () => {
        setActiveModal(null);
        setArchiveDoc(null);
    };

    const bumpArchiv = useCallback(() => {
        setArchivRefreshKey((k) => k + 1);
    }, []);

    const openFromArchive = useCallback((doc: PePapersArchiveDoc, modalId: string) => {
        setArchiveDoc(doc);
        setActiveModal(modalId);
    }, []);

    return (
        <>
            <PePapersPageStyles />
            <div className="pepapers-shell min-h-[calc(100vh-12rem)] bg-pe-card dark:bg-[#0f0f10] rounded-2xl border border-pe-border dark:border-white/5 overflow-hidden">
            <div className="relative carbon-weave-bg px-6 md:px-10 pt-10 pb-8 border-b border-pe-border dark:border-white/5">
                <div className="absolute inset-0 bg-gradient-to-br from-[#E6C299]/[0.04] via-transparent to-transparent pointer-events-none" />
                <div className="relative z-10 max-w-4xl flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full border border-[#E6C299]/25 bg-[#E6C299]/10">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#E6C299] shadow-[0_0_8px_rgba(230,194,153,0.8)]" />
                            <span className="text-[10px] font-black uppercase tracking-[0.25em] text-pe-accent-text dark:text-[#E6C299]">Mitarbeiter & Verträge</span>
                        </div>
                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-pe-text dark:text-white tracking-tight mb-3">
                            <span className="text-pe-accent-text dark:text-[#E6C299] italic">PE</span>Papers
                        </h1>
                        <p className="text-pe-muted dark:text-slate-400 text-sm md:text-base leading-relaxed max-w-2xl">
                            Mitarbeiter & Verträge – Einfach, kontrolliert und rechtssicher auf Knopfdruck erstellt.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setArchivOpen(true)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/10 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/20"
                        >
                            <Archive className="h-3.5 w-3.5" />
                            Archiv
                        </button>
                        <button
                            type="button"
                            onClick={() => openPremiumModal('pe-papers')}
                            className="rounded-lg border border-[#E6C299]/40 bg-[#E6C299]/10 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-pe-accent-text dark:text-[#E6C299] hover:bg-[#E6C299]/20"
                        >
                            Tarifübersicht
                        </button>
                    </div>
                </div>
            </div>

            <div className="px-6 md:px-10 py-8 md:py-10">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {PE_PAPERS_MODULES.map((module) => (
                        <article
                            key={module.id}
                            onClick={() => {
                                setArchiveDoc(null);
                                setActiveModal(module.id);
                            }}
                            className={`pepapers-module-card carbon-panel rounded-2xl p-7 md:p-8 flex flex-col justify-between min-h-[220px] cursor-pointer transition-all duration-300 hover:border-[#E6C299]/40 bg-pe-card dark:bg-[#030303] border border-pe-border dark:border-[#2a2a2a] ${
                                module.warnAccent ? 'pepapers-card-warn' : ''
                            }`}
                        >
                            <span
                                className={`absolute top-4 right-4 z-20 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${
                                    module.warnAccent
                                        ? 'pepapers-badge-warn text-rose-700 dark:text-rose-400 bg-rose-500/15 border-rose-500/40 dark:border-rose-500/30'
                                        : 'text-amber-800 dark:text-amber-500/80 bg-amber-500/15 border-amber-600/40 dark:border-amber-500/20 font-bold'
                                }`}
                            >
                                {module.badgeLabel || 'Dokument'}
                            </span>

                            <div className="carbon-content flex flex-col flex-1">
                                <h2 className="pepapers-title-strip text-pe-text dark:text-white font-black text-base md:text-lg uppercase tracking-wide leading-snug pr-16 mb-3 px-3 py-2 carbon-weave-bg rounded-lg -mx-1 flex items-start gap-2 min-w-0">
                                    {module.showShieldIcon ? (
                                        <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-rose-600 dark:text-rose-400" aria-hidden />
                                    ) : null}
                                    <span className="min-w-0 break-words hyphens-auto">
                                        {module.titleLines?.length
                                            ? module.titleLines.map((line, idx) => (
                                                  <React.Fragment key={`${module.id}-t-${idx}`}>
                                                      {idx > 0 ? <br /> : null}
                                                      {line}
                                                  </React.Fragment>
                                              ))
                                            : module.title}
                                    </span>
                                </h2>
                                <p className="pepapers-desc text-pe-muted dark:text-neutral-400 text-sm leading-relaxed flex-1">
                                    {module.description}
                                </p>
                                {module.storageHint ? (
                                    <PePapersStorageHint
                                        hint={module.storageHint}
                                        variant="card"
                                        className="mt-3"
                                    />
                                ) : null}
                                <div className="mt-6 pt-4 border-t border-stone-300 dark:border-white/5">
                                    <span
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setArchiveDoc(null);
                                            setActiveModal(module.id);
                                        }}
                                        className={`block w-full py-2.5 bg-transparent border text-xs uppercase tracking-widest transition-colors rounded-lg text-center font-medium cursor-pointer ${
                                            module.warnAccent
                                                ? 'pepapers-cta-warn border-rose-500/40 text-rose-700 dark:text-rose-300 hover:bg-rose-500/10'
                                                : 'pepapers-cta-default border-pe-border-strong dark:border-[#333] hover:bg-pe-hover dark:hover:bg-[#0a0b10] text-stone-700 dark:text-neutral-300'
                                        }`}
                                    >
                                        {module.statusText}
                                    </span>
                                </div>
                            </div>
                        </article>
                    ))}
                </div>
            </div>

            <ArbeitsvertragModal
                isOpen={activeModal === 'arbeitsvertrag'}
                onClose={closeModal}
                initialData={activeModal === 'arbeitsvertrag' ? archiveDoc : null}
                onArchived={bumpArchiv}
            />
            <KuendigungModal
                isOpen={activeModal === 'kuendigung'}
                onClose={closeModal}
                initialData={activeModal === 'kuendigung' ? archiveDoc : null}
                onArchived={bumpArchiv}
            />
            <UnwiderruflicheFreistellungModal
                isOpen={activeModal === 'unwiderrufliche-freistellung'}
                onClose={closeModal}
                initialData={activeModal === 'unwiderrufliche-freistellung' ? archiveDoc : null}
                onArchived={bumpArchiv}
            />
            <AbmahnungModal
                isOpen={activeModal === 'abmahnung'}
                onClose={closeModal}
                initialData={activeModal === 'abmahnung' ? archiveDoc : null}
                onArchived={bumpArchiv}
            />
            <ArbeitszeugnisModal
                isOpen={activeModal === 'arbeitszeugnis'}
                onClose={closeModal}
                initialData={activeModal === 'arbeitszeugnis' ? archiveDoc : null}
                onArchived={bumpArchiv}
            />
            <ElternzeitModal
                isOpen={activeModal === 'urlaubskuerzung'}
                onClose={closeModal}
                initialData={activeModal === 'urlaubskuerzung' ? archiveDoc : null}
                onArchived={bumpArchiv}
            />
            <VertragsAnpassungKombiModal
                isOpen={activeModal === 'vertragsanpassung'}
                onClose={closeModal}
                initialData={activeModal === 'vertragsanpassung' ? archiveDoc : null}
                onArchived={bumpArchiv}
            />
            <FortbildungModal
                isOpen={activeModal === 'fortbildungsrueckzahlung'}
                onClose={closeModal}
                initialData={activeModal === 'fortbildungsrueckzahlung' ? archiveDoc : null}
                onArchived={bumpArchiv}
            />
            <ModellReleaseModal
                isOpen={activeModal === 'modell-release'}
                onClose={closeModal}
                initialData={activeModal === 'modell-release' ? archiveDoc : null}
                onArchived={bumpArchiv}
            />
            <MitfuehrpflichtModal
                isOpen={activeModal === 'mitfuehrpflicht-zeiterfassung'}
                onClose={closeModal}
                initialData={activeModal === 'mitfuehrpflicht-zeiterfassung' ? archiveDoc : null}
                onArchived={bumpArchiv}
            />
            <PEPapersInstructionModal
                isOpen={activeModal === 'dienstanweisung-social-media'}
                onClose={closeModal}
                initialData={activeModal === 'dienstanweisung-social-media' ? archiveDoc : null}
                onArchived={bumpArchiv}
            />
            <PEInvoiceModal
                isOpen={activeModal === 'pe-invoice'}
                onClose={closeModal}
                initialData={activeModal === 'pe-invoice' ? archiveDoc : null}
                onArchived={bumpArchiv}
            />
            <ConsultationQuoteModal
                isOpen={activeModal === 'consultation-quote'}
                onClose={closeModal}
                initialData={activeModal === 'consultation-quote' ? archiveDoc : null}
                onArchived={bumpArchiv}
            />
            <BgwAssessmentModal
                isOpen={activeModal === 'bgw'}
                onClose={closeModal}
                initialData={activeModal === 'bgw' ? archiveDoc : null}
                onArchived={bumpArchiv}
            />
            <BgwUvvUnterweisungModal
                isOpen={activeModal === 'bgw-uvv-unterweisung'}
                onClose={closeModal}
                initialData={activeModal === 'bgw-uvv-unterweisung' ? archiveDoc : null}
                onArchived={bumpArchiv}
            />
            <DsgvoVvtModal
                isOpen={activeModal === 'dsgvo'}
                onClose={closeModal}
                initialData={activeModal === 'dsgvo' ? archiveDoc : null}
                onArchived={bumpArchiv}
            />
            <GobdModal
                isOpen={activeModal === 'gobd'}
                onClose={closeModal}
                initialData={activeModal === 'gobd' ? archiveDoc : null}
                onArchived={bumpArchiv}
            />
            <PeSachbezugModal
                isOpen={activeModal === 'sachbezug-50'}
                onClose={closeModal}
                initialData={activeModal === 'sachbezug-50' ? archiveDoc : null}
                onArchived={bumpArchiv}
            />

            <PePapersArchivPanel
                isOpen={archivOpen}
                onClose={() => setArchivOpen(false)}
                onOpenDocument={openFromArchive}
                refreshKey={archivRefreshKey}
            />
            </div>
        </>
    );
};

export default PePapersPage;
